import { readonly, type Ref } from 'vue';

import { UserRole } from '~/apiClient';

type UseAuthReturn = ReturnType<typeof useAuth>;
type UseAuthStateReturn = ReturnType<typeof useAuthState>;

type UseAuthUtilsReturn = UseAuthReturn & {
    state: UseAuthStateReturn;
    logout: () => void;
    isLoggedIn: Readonly<Ref<boolean>>;
    isSalesRep: Readonly<Ref<boolean>>;
    isCustomerService: Readonly<Ref<boolean>>;
};

export const useAuthUtils = (): UseAuthUtilsReturn => {
    const auth = useAuth();
    const authState = useAuthState();
    const { clearToken, data: stateData, token } = useAuthState();

    const logout = (): void => {
        clearToken();
        stateData.value = null;
    };

    const isLoggedIn = computed(() => {
        if (auth.status.value === 'authenticated') return true;
        else if (auth.status.value === 'unauthenticated') return false;

        return auth.status.value === 'loading' && !!token.value;
    });

    const isSalesRep = computed(() => stateData.value?.user?.role === UserRole.SalesRep);

    const isCustomerService = computed(() => stateData.value?.user?.role === UserRole.CustomerService);

    return {
        ...auth,
        state: authState,
        logout,
        isLoggedIn: readonly(isLoggedIn),
        isSalesRep: readonly(isSalesRep),
        isCustomerService: readonly(isCustomerService),
    };
};
